"use client";

import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { useTranslation } from "@/lib/hooks/use-translation";
import { cn } from "@/lib/utils";

const DIALOG_CONTENT_CLASS = [
  "surface-card data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  "sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95",
  "pointer-events-auto relative grid max-h-[calc(100dvh-2rem)] w-full",
  "max-w-[calc(100%-2rem)] gap-2 overflow-y-auto rounded-lg border p-3 shadow-elevation3 duration-200 outline-none sm:max-w-lg",
].join(" ");

const DIALOG_CLOSE_CLASS = [
  "ring-offset-background focus:ring-ring data-[state=open]:bg-accent",
  "data-[state=open]:text-muted-foreground absolute top-3 right-3 z-20 flex size-8 items-center justify-center rounded-md bg-card opacity-90 transition-opacity",
  "hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
  "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
].join(" ");

function eventTargetElement(event: Event): Element | null {
  if (event.target instanceof Element) {
    return event.target;
  }
  const detail = (event as Event & { detail?: { originalEvent?: Event } }).detail;
  const original = detail?.originalEvent;
  if (original instanceof Event && original.target instanceof Element) {
    return original.target;
  }
  return null;
}

function isRadixPortaledSelectTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return target.closest(
    "[data-slot=popover-content], [data-slot=popover-trigger], [data-slot=command-item], [cmdk-item], [cmdk-input], [cmdk-list], [cmdk-root]",
  ) !== null;
}

function preventDismissOnPortaledSelect(event: Event): void {
  if (isRadixPortaledSelectTarget(eventTargetElement(event))) {
    event.preventDefault();
  }
}

function preventDialogOutsideDismiss(event: Event): void {
  event.preventDefault();
}

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, style, ...props }, ref) => {
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    zIndex: 110,
    backgroundColor: "rgb(15 23 42 / 0.42)",
    backdropFilter: "blur(8px)",
    ...style,
  };
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      data-slot="dialog-overlay"
      className={cn("data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0", className)}
      style={overlayStyle}
      {...props}
    />
  );
});
DialogOverlay.displayName = "DialogOverlay";

function DialogContent({
  className,
  children,
  showCloseButton = true,
  lockOutsideDismiss = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  lockOutsideDismiss?: boolean;
}) {
  const { t } = useTranslation();
  const outsideDismissHandler = lockOutsideDismiss ? preventDialogOutsideDismiss : preventDismissOnPortaledSelect;
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <div
        data-slot="dialog-center"
        className="pointer-events-none fixed inset-0 z-[111] flex items-center justify-center p-4"
      >
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            DIALOG_CONTENT_CLASS,
            className,
          )}
          onPointerDownOutside={outsideDismissHandler}
          onInteractOutside={outsideDismissHandler}
          onFocusOutside={outsideDismissHandler}
          {...props}
        >
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className={DIALOG_CLOSE_CLASS}
            >
              <XIcon />
              <span className="sr-only">{t("common.close")}</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </div>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-header" className={cn("flex flex-col gap-1 text-center sm:text-left", className)} {...props} />;
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div data-slot="dialog-footer" className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)} {...props} />;
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title data-slot="dialog-title" className={cn("text-base leading-none font-semibold", className)} {...props} />;
}

function DialogDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn("text-muted-foreground text-xs leading-snug", className)} {...props} />;
}

export { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger, isRadixPortaledSelectTarget, preventDialogOutsideDismiss };
